import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import CountryProp from '../../../commonComponents/forms/Address/CountryProp';
import { compose, handleImageUpload } from '../../../services/utilityFunctions';
import withCSRF from '../../../commonComponents/withCSRF';
import translate from '../../../commonComponents/translate';
import Form from '../../../commonComponents/forms/Form';
import FormFooter from '../../../commonComponents/forms/FormFooter';
import FormHeader from '../../../commonComponents/forms/FormHeader';
import FormInput from '../../../commonComponents/forms/FormInput';
import Request from '../../../services/request';
import Notifications from '../../../services/notifications';
import HardSkillProp from '../propTypes/hardSkillProp';
import GameRecommendationProp from '../propTypes/gameRecommendationProp';
import Separator from '../../../commonComponents/forms/Separator';
import ImageInput from '../../../commonComponents/forms/ImageInput';
import ConfigsContext from '../../../contextProviders/ConfigsContext';

function RecommendationForm(props) {
  const { t, _csrf, isEdit, countries, languages, hardSkills, recommendation } =
    props;
  const { organization } = useContext(ConfigsContext);

  const initialSoftSkills = recommendation.softSkills || {};
  const initialState = {
    firstName: recommendation.firstName || '',
    lastName: recommendation.lastName || '',
    image: {
      blob: null,
      fileName: '',
    },
    email: recommendation.email || '',
    phone: recommendation.phone || '',
    address: recommendation.address,
    nationality: recommendation.nationality
      ? recommendation.nationality.code
      : '',
    birthday: recommendation.birthday,
    genre: recommendation.genre || '',
    hardSkills: (recommendation.hardSkills || []).map(hs => hs._id),
    experienceTime: initialSoftSkills.experienceTime || '',
    competenciesDomains: initialSoftSkills.competenciesDomains || [],
    languages: (initialSoftSkills.languages || []).map(l => l.code),
  };
  const [state, setState] = useState(initialState);
  const [imageTouched, setImageTouched] = useState(false);

  const imageUpload = async () => {
    if (!imageTouched) {
      return recommendation.image;
    }
    return handleImageUpload(t, _csrf, state.image);
  };

  const handleSubmit = async () => {
    const image = await imageUpload();
    const params = {
      referencedBy: recommendation.referencedBy,
      game: recommendation.game,
      firstName: state.firstName,
      lastName: state.lastName,
      image,
      email: state.email,
      phone: state.phone,
      address: state.address,
      nationality: state.nationality
        ? countries.find(c => c.code === state.nationality)
        : null,
      birthday: state.birthday || null,
      genre: state.genre || null,
      hardSkills: state.hardSkills
        .map(id => hardSkills.find(hs => hs._id === id))
        .map(hs => ({ ...hs, value: true })),
      softSkills: {
        languages: state.languages.map(code =>
          languages.find(l => l.code === code),
        ),
        experienceTime: state.experienceTime || 0,
        competenciesDomains: state.competenciesDomains,
      },
    };
    if (isEdit) {
      params._id = recommendation._id;
    }

    const url = isEdit
      ? `/challenges/recommendations/${recommendation._id}`
      : '/challenges/recommendations';

    const successMessage = isEdit ? t('success_update') : t('success_create');

    const requestResult = isEdit
      ? new Request(_csrf).put(url, params)
      : new Request(_csrf).post(url, params);

    requestResult
      .done(({ data }) => {
        Notifications.showNotificationSuccess(t('success'), successMessage);
        window.location.href = `/challenges/${data.game}/recommendations`;
      })
      .fail(err => {
        const error = isEdit
          ? 'error_recommendationUpdate'
          : 'error_recommendationCreate';
        Notifications.showErrorFromRequest(err, error);
      });
  };

  const handleChange = field => value => {
    if (field === 'image') {
      setImageTouched(true);
    }
    setState(prevState => ({ ...prevState, [field]: value }));
  };

  const handleAddOption = field => value =>
    setState(prevState => ({
      ...prevState,
      [field]: prevState[field].concat(value),
    }));

  const handleRemoveOption = field => value =>
    setState(prevState => ({
      ...prevState,
      [field]: prevState[field].filter(v => v !== value),
    }));

  const createInput = (field, typeParam) => {
    const type = typeParam || 'text';

    const isContactField = field === 'email' || field === 'phone';

    return (
      <FormInput
        id={field}
        name={field}
        value={state[field]}
        onChange={handleChange(field)}
        label={`${t(`recommendationDetails_${field}`)}${
          isContactField ? '*' : ''
        }`}
        placeholder={t(`recommendationDetails_${field}Placeholder`)}
        labelClassName="col-2"
        containerClassName="col-7"
        type={type}
        groupProps={{ className: 'row' }}
        inputClassName={isContactField ? 'contact-group' : ''}
        inputProps={
          field === 'address'
            ? {
                defaultValue: state.address ? state.address.address : undefined,
                updateValue: state.address ? state.address.address : undefined,
                maxLength: 100,
              }
            : undefined
        }
      />
    );
  };

  const createSelect = (field, items, multiple) => (
    <FormInput
      id={field}
      name={field}
      value={state[field]}
      onChange={multiple ? handleAddOption(field) : handleChange(field)}
      label={t(`recommendationDetails_${field}`)}
      labelClassName="col-2"
      containerClassName="col-7"
      type="select2"
      placeholder={t(`recommendationDetails_${field}Placeholder`)}
      groupProps={{ className: 'row' }}
      inputProps={{
        onUnselect: multiple ? handleRemoveOption(field) : undefined,
        multiple,
        items,
      }}
    />
  );

  // Dynamic validation rules that change based on current state
  const getValidationRules = () => ({
    experienceTime: { min: 0 },
    email: {
      require_from_group: [1, '.contact-group'],
      email: true,
    },
    phone: {
      require_from_group: [1, '.contact-group'],
    },
  });

  // Custom error messages
  const getCustomMessages = () => ({
    email: {
      require_from_group: t('email_or_phone_required'),
    },
    phone: {
      require_from_group: t('email_or_phone_required'),
    },
  });

  return (
    <Form
      className="m-form m-form--fit m-form--label-align-right"
      id="recommendationForm"
      submitMethod={handleSubmit}
      validationRules={getValidationRules()}
      customMessages={getCustomMessages()}
    >
      {submitHandler => (
        <>
          <div className="m-portlet__body">
            <FormHeader title={t('info')} />
            <ImageInput
              initialValue={
                recommendation &&
                recommendation.image &&
                recommendation.image.url
              }
              placeholder="/images/no_user.png"
              onChange={handleChange('image')}
              label={t('recommendationDetails_image')}
            />
            {createInput('firstName')}
            {createInput('lastName')}
            {createInput('birthday', 'date')}
            {createSelect(
              'genre',
              ['male', 'female'].map(s => ({ id: s, text: t(s) })),
            )}
            {createSelect(
              'nationality',
              countries.map(c => ({
                id: c.code,
                text: `${c.flagEmoji} ${c.name}`,
              })),
            )}
            {createInput('address', 'googleSearch')}
            {createInput('email')}
            {createInput('phone')}
            <Separator />
            <FormHeader title={t('recommendationDetails_skills')} />
            {createSelect(
              'languages',
              languages.map(c => ({
                id: c.code,
                text: c.name,
              })),
              true,
            )}
            {createInput('experienceTime', 'number')}
            {createSelect(
              'competenciesDomains',
              organization?.competenciesDomains?.map(cd => ({
                id: cd._id,
                text: cd.name,
              })) || [],
              true,
            )}
            {createSelect(
              'hardSkills',
              hardSkills.map(hs => ({ id: hs._id, text: hs.name })),
              true,
            )}
          </div>
          <FormFooter submitHandler={submitHandler} />
        </>
      )}
    </Form>
  );
}

RecommendationForm.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  isEdit: PropTypes.bool.isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  languages: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  hardSkills: PropTypes.arrayOf(PropTypes.shape(HardSkillProp.propType))
    .isRequired,
  recommendation: PropTypes.shape(GameRecommendationProp.propType).isRequired,
};

export default compose(withCSRF, translate)(RecommendationForm);
